import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/BaseDropdown/dropdown-base'
import { TabButton } from './TabButton'
import { TabConfig, BaseTabsProps } from './types'

// Re-export for backwards compatibility
export type ResponsiveTabConfig<T> = TabConfig<T>

// Minimum width for the overflow button
const OVERFLOW_BUTTON_WIDTH = 48

export function ResponsiveTabs<T>({
  tabs,
  selectedTab,
  setSelectedTab,
  className = '',
}: BaseTabsProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureContainerRef = useRef<HTMLDivElement>(null)
  const [visibleCount, setVisibleCount] = useState(tabs.length)
  const [tabWidths, setTabWidths] = useState<number[]>([])
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMeasured, setIsMeasured] = useState(false)

  // Measure all tab widths once on mount using the hidden measurement container
  useEffect(() => {
    const measureTabs = () => {
      if (!measureContainerRef.current) return

      const buttons = measureContainerRef.current.querySelectorAll('button')
      const widths: number[] = []

      buttons.forEach((btn) => {
        widths.push(btn.getBoundingClientRect().width)
      })

      if (widths.length === tabs.length) {
        setTabWidths(widths)
        setIsMeasured(true)
      }
    }

    // Measure after a short delay to ensure tabs are rendered
    const timer = setTimeout(measureTabs, 50)
    return () => clearTimeout(timer)
  }, [tabs])

  // Calculate visible tabs based on container width
  const calculateVisibleTabs = useCallback(() => {
    if (!containerRef.current || tabWidths.length === 0) return

    const containerWidth = containerRef.current.getBoundingClientRect().width
    let totalWidth = 0
    let count = 0

    // First pass: check if all tabs fit
    let allFit = true
    let tempTotal = 0
    for (let i = 0; i < tabWidths.length; i++) {
      tempTotal += tabWidths[i]
      if (tempTotal > containerWidth) {
        allFit = false
        break
      }
    }

    if (allFit) {
      setVisibleCount(tabs.length)
      return
    }

    // Not all tabs fit, need overflow button
    const reservedWidth = containerWidth - OVERFLOW_BUTTON_WIDTH

    for (let i = 0; i < tabWidths.length; i++) {
      if (totalWidth + tabWidths[i] <= reservedWidth) {
        totalWidth += tabWidths[i]
        count++
      } else {
        break
      }
    }

    // Ensure at least one tab is visible
    setVisibleCount(Math.max(1, count))
  }, [tabWidths, tabs.length])

  // Recalculate on resize
  useEffect(() => {
    if (tabWidths.length === 0) return

    calculateVisibleTabs()

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleTabs()
    })

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current)
    }

    return () => resizeObserver.disconnect()
  }, [calculateVisibleTabs, tabWidths])

  const visibleTabs = useMemo(
    () => tabs.slice(0, visibleCount),
    [tabs, visibleCount]
  )
  const hiddenTabs = useMemo(
    () => tabs.slice(visibleCount),
    [tabs, visibleCount]
  )
  const hasHiddenTabs = hiddenTabs.length > 0

  // Check if selected tab is in the hidden tabs
  const selectedInHidden = useMemo(
    () => hiddenTabs.some((tab) => tab.key === selectedTab),
    [hiddenTabs, selectedTab]
  )

  const renderTabs = (tabsToRender: TabConfig<T>[], isInteractive: boolean) =>
    tabsToRender.map(({ key, title, badge }) => (
      <TabButton
        key={isInteractive ? String(key) : `measure-${String(key)}`}
        title={title}
        badge={badge}
        isActive={isInteractive && selectedTab === key}
        onClick={() => isInteractive && setSelectedTab(key)}
        flexMode="content"
      />
    ))

  return (
    <>
      {/* Hidden measurement container - positioned off-screen */}
      <div
        ref={measureContainerRef}
        className="flex absolute -left-[9999px] whitespace-nowrap"
        aria-hidden="true"
        style={{ visibility: 'hidden', pointerEvents: 'none' }}
      >
        {renderTabs(tabs, false)}
      </div>

      {/* Visible tabs container */}
      <div ref={containerRef} className={`flex w-full ${className}`}>
        {/* Only render visible tabs after measurement */}
        {isMeasured && renderTabs(visibleTabs, true)}

        {/* Show all tabs before measurement completes */}
        {!isMeasured && renderTabs(tabs, true)}

        {/* Overflow dropdown */}
        {isMeasured && hasHiddenTabs && (
          <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button
                className={`
                  flex-shrink-0 p-3 text-base text-center relative
                  transition-colors duration-200
                  ${
                    selectedInHidden
                      ? 'text-[#191D23] font-semibold border-b-2 border-green-700'
                      : 'text-[#191D23] font-light hover:text-[#191D23]'
                  }
                `}
                aria-label="More tabs"
              >
                <span className="text-[#191D23] font-light text-base leading-none">
                  ...
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-[176px] p-2 flex flex-col gap-2 bg-white border border-[#8EA69B] shadow-[0px_4px_4px_rgba(5,70,40,0.15)] rounded-lg"
            >
              {hiddenTabs.map(({ key, title, icon: Icon }) => {
                const isActive = selectedTab === key
                return (
                  <DropdownMenuItem
                    key={String(key)}
                    onClick={() => {
                      setSelectedTab(key)
                      setIsDropdownOpen(false)
                    }}
                    className={`
                      flex items-center gap-2 p-1 h-[28px] rounded cursor-pointer
                      ${isActive ? 'font-medium' : 'font-normal'}
                      text-[#101828] text-sm leading-5
                      hover:bg-[#D1DCD7]
                    `}
                  >
                    {Icon && (
                      <Icon
                        size={20}
                        className="text-[#667085] flex-shrink-0"
                      />
                    )}
                    <span>{title}</span>
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </>
  )
}
